// 1:1 port from age-of-mcp v3 goldsmith — a small limewashed hut under a gold
// (ROOF_PRECIOUS) gable roof, with a big gold coin raised on a sign post at the
// yard corner and the anvil out in the open forecourt on a timber stump, where
// the smith hammers in full view. A glowing FIRE forge mouth opens in the hut
// front, and a small stack of minted coins cools beside the anvil.
import { defineModel, benchWork, styleV3, type Part } from "../../lib";

const {
  box, cyl, gable, poles, slab,
  FIRE, PLASTER, ROOF_PRECIOUS, STEEL, TIMBER,
} = styleV3;

const BASE = 0.12;            // foundation slab top
const HX = -0.1, HZ = -0.12;  // the hut, set back from the forecourt
const WALL_H = 0.55;

// The hut walls, `h` tall on the foundation.
function hut(h: number): Part {
  return box(0.6, h, 0.56, PLASTER, [HX, BASE + h / 2, HZ]);
}

// The gold gable roof — ONE part, the precious-goods code (stage 3 only).
function roof(): Part {
  return gable(0.72, 0.68, 0.3, ROOF_PRECIOUS, HX, BASE + WALL_H, HZ, "z");
}

// The plank door and the glowing forge mouth on the hut front.
function front(): Part[] {
  const zf = HZ + 0.28 + 0.02;
  return [
    box(0.2, 0.36, 0.05, TIMBER, [HX - 0.15, BASE + 0.18, zf]),
    box(0.2, 0.16, 0.05, FIRE,   [HX + 0.15, BASE + 0.24, zf]),
  ];
}

// The big gold coin raised on a post at the yard corner — the shop sign.
function coinSign(): Part[] {
  return [
    cyl(0.028, 0.032, 0.55, 5, TIMBER, [0.36, BASE + 0.275, -0.3]),
    cyl(0.15, 0.15, 0.04, 12, ROOF_PRECIOUS, [0.36, BASE + 0.66, -0.28], [Math.PI / 2, 0, 0]),
  ];
}

// The anvil on its stump, out in the open forecourt.
function anvil(): Part[] {
  return [
    cyl(0.07, 0.09, 0.16, 6, TIMBER, [0.18, BASE + 0.08, 0.32]),
    box(0.18, 0.07, 0.09, STEEL, [0.18, BASE + 0.2, 0.32]),
  ];
}

// A small stack of minted gold coins cooling beside the anvil.
function coins(): Part[] {
  return [
    cyl(0.07, 0.07, 0.04, 10, ROOF_PRECIOUS, [-0.06, BASE + 0.02, 0.4]),
    cyl(0.07, 0.07, 0.04, 10, ROOF_PRECIOUS, [-0.06, BASE + 0.06, 0.4]),
    cyl(0.06, 0.06, 0.04, 10, ROOF_PRECIOUS, [0.02, BASE + 0.02, 0.32]),
  ];
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(0.88, 0.88)];
  if (s === 1) return [slab(0.88, 0.88), hut(0.26), ...poles(0.82, 0.82, 0.6)];
  if (s === 2) return [slab(0.88, 0.88), hut(WALL_H), ...front(), ...anvil(), ...poles(0.82, 0.82, 1.0)];
  return [
    slab(0.88, 0.88), hut(WALL_H), roof(), ...front(),
    ...coinSign(), ...anvil(), ...coins(),
  ];
}

export const goldsmithV3 = defineModel({
  id: "goldsmith-v3",
  tiles: { w: 2, h: 2 },
  batchId: "45-optimized",
  name: "Goldsmith (v3)",
  tags: ["building","goldsmith","low-poly","age-of-mcp","v3"],
  summary: "Low-poly goldsmith.",
  description: "A goldsmith's forge that refines raw gold into minted coins.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
  stations: [
    { id: "bench", position: [0.18, 0.12, 0.55], facing: Math.PI },
  ],
  interactions: [
    { id: "work", label: "At the bench", station: "bench", variants: ["complete"], ...benchWork() },
  ],
});
